import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { parseIframeConfig, IframeConfig, isInIframe } from '../utils/iframeConfig'
import { createColorsWithOverrides } from '../theme/colors'

interface IframeContextType {
  config: IframeConfig
  customColors: ReturnType<typeof createColorsWithOverrides>
  isIframe: boolean
}

const IframeContext = createContext<IframeContextType | undefined>(undefined)

interface IframeProviderProps {
  children: ReactNode
}

export function IframeProvider({ children }: IframeProviderProps) {
  const [config, setConfig] = useState<IframeConfig>(() => parseIframeConfig())
  const [isIframe] = useState(() => isInIframe())
  
  // Create custom colors based on config
  const customColors = createColorsWithOverrides(config.colors)

  // Listen for URL changes (for dynamic updates)
  useEffect(() => {
    const handleUrlChange = () => {
      setConfig(parseIframeConfig())
    }

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleUrlChange)
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleUrlChange)

    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      window.removeEventListener('hashchange', handleUrlChange)
    }
  }, [])

  const value: IframeContextType = {
    config,
    customColors,
    isIframe
  }

  return (
    <IframeContext.Provider value={value}>
      {children}
    </IframeContext.Provider>
  )
}

export function useIframe() {
  const context = useContext(IframeContext)
  if (context === undefined) {
    throw new Error('useIframe must be used within an IframeProvider')
  }
  return context
}

// Hook for easy access to custom colors
export function useCustomColors() {
  const { customColors } = useIframe()
  return customColors
}
